<template>
    <Widget :title="$t('components.WebMDDashboard.ContactMetricsTitle')" :loading="loading" :report-mode="reportMode">
        <v-row align="center" justify="space-between" style="width: 100%">
            <v-col cols="12" sm="4">
                <span class="d-inline-block mx-4 text-caption ddwhite--text" v-for="(item, index) in Object.values(legend)" :key="index">
                    <span class="circle" :style="{ backgroundColor: item.color }"></span> 
                    <span class="label">{{ item.title }}</span>
                </span>
            </v-col>

            <v-col cols="12" sm="4" class="text-right" v-if="!isReport">
                <span class="d-block input-label mr-6 ddgrey--text text-caption">
                    {{ $t("components.Filters.FilterText") }}
                </span>
                <v-select
                    solo
                    dense
                    class="d-inline-block groupby"
                    v-model="groupBy"
                    item-text="hint"
                    item-value="value"
                    :items="[
                        { value: 'all', hint: $t('components.Filters.AllGroupsLabel') },
                        { value: 'website_clicks', hint: $t('components.WebMDDashboard.WebsiteClicksLabel') },
                        { value: 'calls', hint: $t('components.WebMDDashboard.CallsLabel') },
                        { value: 'chatnow_clicks', hint: $t('components.WebMDDashboard.ChatNowClicksLabel') } ]"
                    :label="$t('components.Filters.FilterText')"
                    @change="filterData(data)"/>
            </v-col>

            <v-col cols="12" sm="4" class="text-right" v-if="!isReport">
                <span class="d-block input-label mr-6 ddgrey--text text-caption">
                    {{ $t("components.Filters.GroupText") }}
                </span>
                <v-select
                    solo
                    dense
                    class="d-inline-block groupby"
                    v-model="filterBy"
                    item-text="hint"
                    item-value="value"
                    :items="[
                        { value: 'month', hint: $t('components.Filters.MonthLabel') },
                        { value: 'month_cumulative', hint: $t('components.Filters.MonthCumulativeLabel') },
                        { value: 'day', hint: $t('components.Filters.DayLabel') } ]"
                    :label="$t('components.Filters.GroupText')"
                    @change="filterData(data)"/>
            </v-col>
        </v-row>
        <StackedBarChart :stats="stats" :styles="styles"/>
    </Widget>
</template>

<script>
/** 
 * ContactMetricsChart wraps a StackedBarChart and contains the functions to parse
 * and format data from the API.
 * 
 * It represents the number of chats versus the number of referrals grouped
 * by month, day of the week or hour of the day.
 */

import { DayLabels } from '@/mixins/TimeLabels';

import Widget from '@/components/dashboard/Widget';

import StackedBarChart from '@/components/dashboard/charts/StackedBarChart';

export default {
    name: 'ContactMetricsChart',
    data: () => ({ 
        stats: {},
        styles: {
            position: 'relative',
            width: '100%',
            padding: '24px 24px 0',
        },
        groupBy: 'all',
        filterBy: 'month',
        legend: [],
    }),
    props: {
        loading: {
            type: Boolean,
            default: true
        },
        chartData: {
            type: Object,
            required: true
        },
        reportMode: {
            type: String,
            default: null
        },
        appVersion: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        isReport() { return this.reportMode !== null },
        colors() {
            return this.$vuetify.theme.isDark ? 
                this.$vuetify.theme.themes.dark : this.$vuetify.theme.themes.light;
        },
    },
    mounted() {
        this.inflateLegend();
        if (Array.isArray(this.appVersion) && this.appVersion.includes('ContactMetrics_filtered_month_cumulative')) this.filterBy = 'month_cumulative';
        this.updateStats(this.chartData);
        console.log("STATS mounted");
    },
    watch: {
        chartData(newData) {
            this.updateStats(newData);
        },
        appVersion(test) {
            if (Array.isArray(test) && test.includes('ContactMetrics_filtered_month_cumulative')) this.filterBy = 'month_cumulative';
        },
        groupBy() {
            this.updateStats(this.chartData);
        },
        filterBy() {
            this.updateStats(this.chartData);
        }
    },
    methods: {
        inflateLegend() {
            const $t = this.$t.bind(this);
            this.legend = {
                website_clicks: {
                    title: $t("components.WebMDDashboard.WebsiteClicksLabel"),
                    color: this.colors.ddgreen
                },
                calls: {
                    title: $t("components.WebMDDashboard.CallsLabel"),
                    color: this.colors.dddarkblue
                },
                chatnow_clicks: {
                    title: $t("components.WebMDDashboard.ChatNowClicksLabel"),
                    color: "#F6AA53"
                },
            }
        },
        updateStats(data) {
            if (data) {
                const items = this.filterData(data);
                this.stats = this.formatItems(items);
            }
            switch (this.reportMode) {
                case 'weekly':
                    this.filterBy = 'day';
                    break;
                case 'monthly':
                    this.filterBy = 'month';
                    break;
            }
        },
        filterData(data) {
            const $t = this.$t.bind(this);
            const items = data[this.filterBy];

            if (items) {
                let formatedItems = {};
                let labels = [];

                switch (this.filterBy) {
                    case 'month':
                    case 'month_cumulative':
                        labels = Object.keys(items);
                        break;
                    case 'day': 
                        labels = DayLabels.map(l => $t(`components.Filters.${l}`));
                        break;
                }

                const keys = Object.keys(items);
                const { length } = keys;
                for (let i = 0; i < length; i++) {
                    const key = keys[i];
                    const label = labels[i];
                    formatedItems[label] = items[key];
                }
                return formatedItems;
            }
            
            return {};
        },
        formatItems(items) {
            const $t = this.$t.bind(this);

            const labels = [...Object.keys(items)];
            const datasets = [];

            const websiteClicks = {
                barPercentage: 0.5,
                label: $t("components.WebMDDashboard.WebsiteClicksLabel"),
                backgroundColor: this.colors.ddgreen,
                data: []
            }

            const calls = {
                barPercentage: 0.5,
                label: $t("components.WebMDDashboard.CallsLabel"),
                backgroundColor: this.colors.dddarkblue,
                data: []
            }

            const chatNowClicks = {
                barPercentage: 0.5,
                label: $t("components.WebMDDashboard.ChatNowClicksLabel"),
                backgroundColor: "#F6AA53",
                data: []
            }

            const { length } = labels;
            for (let i = 0; i < length; i++) {
                const item = items[labels[i]];
                calls.data.push(item.calls);
                websiteClicks.data.push(item.website_clicks);
                chatNowClicks.data.push(item.chatnow_clicks);
            }

            const { groupBy } = this;
            switch (groupBy) {
                case 'website_clicks':
                    datasets.push(websiteClicks);
                    break;
                case 'calls':
                    datasets.push(calls);
                    break;
                case 'chatnow_clicks':
                    datasets.push(chatNowClicks);
                    break;
                case 'all':
                    datasets.push(websiteClicks);
                    datasets.push(calls);
                    datasets.push(chatNowClicks);
                    break;
            }

            return { labels, datasets };
        }
    },
    components: { Widget, StackedBarChart }
}
</script>

<style>
    .circle {
        display: inline-block;
        vertical-align: top;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin: 4px 12px;
    }

    .label {font-size:  1.2em !important;}
    .input-label.input-label {font-size:  1.02em !important;}
    
    .groupby .v-text-field__details { display: none; }
</style>